import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import * as api from "api/api";
import {
	ClientPropertyResource,
	ClientPropertyListItemResource,
	ClientPropertyListResource,
	UpsertClientPropertyResource,
} from "api/resource/client-property";
import { arrayToById, objectToById } from "redux/normalize";
import { AgentControl, isAgentControl } from "type/agent-control";
import { loginReset } from "./authentication";

/**
 * Supplemental listings (ClientProperty, boardId=0) — MAX-3884.
 *
 * Unlike off-market listings, the list endpoint returns lean rows and the edit form loads the full
 * field set via GET-by-id, so state keeps two maps: `listById` (lean rows for the table) and `byId`
 * (full resources for the edit form). Client-level UI flags (agent-page column, office account,
 * settings-notice) come once on the list wrapper.
 */
export interface ClientPropertiesState {
	loading?: boolean;
	error?: string | null;
	loaded: boolean;
	agentControl: AgentControl;
	showAgentPageColumn: boolean;
	supplementalOnFeaturedYn: boolean;
	listById: {
		[id: number]: ClientPropertyListItemResource;
	};
	byId: {
		[id: number]: ClientPropertyResource;
	};
}

const initialState: ClientPropertiesState = {
	loaded: false,
	agentControl: "none",
	showAgentPageColumn: false,
	supplementalOnFeaturedYn: false,
	listById: {},
	byId: {},
};

const fetchClientProperties = createAsyncThunk(
	"clientProperties/fetch",
	async (payload, thunkAPI) => {
		thunkAPI.dispatch(clientPropertiesLoading());
		const result = api.getClientProperties();
		result
			.then((response) => thunkAPI.dispatch(clientPropertiesLoaded(response)))
			.catch((error) => thunkAPI.dispatch(clientPropertiesFailed(error)));
		return result;
	}
);

const fetchClientProperty = createAsyncThunk(
	"clientProperties/fetchOne",
	async (payload: { id: number }, thunkAPI) => {
		const result = api.getClientProperty(payload.id);
		result
			.then((property) => thunkAPI.dispatch(clientPropertyLoaded(property)))
			.catch((error) => thunkAPI.dispatch(clientPropertiesFailed(error)));
		return result;
	}
);

const createClientProperty = createAsyncThunk(
	"clientProperties/create",
	async (payload: { body: UpsertClientPropertyResource }, thunkAPI) => {
		const result = api.createClientProperty(payload.body);
		result
			.then((property) => thunkAPI.dispatch(clientPropertyLoaded(property)))
			.catch((error) => thunkAPI.dispatch(clientPropertiesFailed(error)));
		return result;
	}
);

const updateClientProperty = createAsyncThunk(
	"clientProperties/update",
	async (payload: { id: number; body: UpsertClientPropertyResource }, thunkAPI) => {
		const result = api.updateClientProperty(payload.id, payload.body);
		result
			.then((property) => thunkAPI.dispatch(clientPropertyLoaded(property)))
			.catch((error) => thunkAPI.dispatch(clientPropertiesFailed(error)));
		return result;
	}
);

const deleteClientProperty = createAsyncThunk(
	"clientProperties/delete",
	async (payload: { id: number }, thunkAPI) => {
		thunkAPI.dispatch(clientPropertyDeleted(payload.id));
		const result = api.deleteClientProperty(payload.id);
		result.catch((error) => thunkAPI.dispatch(clientPropertiesFailed(error)));
		return result;
	}
);

export const clientPropertiesSlice = createSlice({
	name: "clientProperties",
	initialState,
	reducers: {
		clientPropertiesLoading(state) {
			state.loading = true;
		},
		clientPropertiesLoaded(state, action: PayloadAction<ClientPropertyListResource>) {
			state.loading = false;
			state.error = null;
			state.loaded = true;
			// MAX-4194: guarded by slice/agent-control-fallback.test.ts; CI skips tests — run it
			// locally before touching this. The fallback covers exactly one deploy window: a bundle carrying this code
			// running against a WAR that predates `agentControl`. ihf-root deploys first, so once
			// it lands the field is always present and this branch is dead — delete it, together
			// with `officeAccount` on both resource interfaces, in the follow-up ticket.
			//
			// It is a membership test rather than `??` on purpose: `??` fires only on
			// null/undefined, so an unrecognised *string* from a newer server would pass through
			// and leave every `=== "dropdown"` / `=== "freeText"` false, blanking the control.
			state.agentControl = isAgentControl(action.payload.agentControl)
				? action.payload.agentControl
				: action.payload.showAgentPageColumn && action.payload.officeAccount
					? "dropdown"
					: action.payload.officeAccount
						? "freeText"
						: "none";
			state.showAgentPageColumn = action.payload.showAgentPageColumn;
			state.supplementalOnFeaturedYn = action.payload.supplementalOnFeaturedYn;
			state.listById = arrayToById(action.payload.items, "id");
		},
		clientPropertyLoaded(state, action: PayloadAction<ClientPropertyResource>) {
			state.loading = false;
			state.error = null;
			state.byId = {
				...state.byId,
				...objectToById(action.payload, "id"),
			};
		},
		clientPropertyDeleted(state, action: PayloadAction<number>) {
			state.loading = false;
			state.error = null;
			const listById = { ...state.listById };
			delete listById[action.payload];
			state.listById = listById;
			const byId = { ...state.byId };
			delete byId[action.payload];
			state.byId = byId;
		},
		clientPropertiesFailed(state, action: PayloadAction<any>) {
			state.loading = false;
			state.error = action.payload && action.payload.message;
		},
	},
	extraReducers(builder) {
		builder.addCase(loginReset, (state) => {
			return {
				...initialState,
				error: state.error,
			};
		});
	},
});

export const {
	clientPropertiesLoading,
	clientPropertiesLoaded,
	clientPropertyLoaded,
	clientPropertyDeleted,
	clientPropertiesFailed,
} = clientPropertiesSlice.actions;

export {
	fetchClientProperties,
	fetchClientProperty,
	createClientProperty,
	updateClientProperty,
	deleteClientProperty,
};
